// El panel lateral del chat: una franja a la derecha que se abre desde
// cualquier pantalla.
//
// EN DESKTOP EMPUJA EL CONTENIDO, NO LO TAPA — y por eso NO va con portal.
// Es el espejo de la sidebar en el borde derecho: un elemento fijo del
// CASCARÓN (hermano de `<Sidebar />` dentro de `DashboardLayout`), no algo
// montado adentro de una página. El panel de contenido ya está clavado a los
// cuatro bordes con `md:fixed top-0 right-0 bottom-0` y anima su `left` cuando
// la sidebar se pliega; acá se le suma el `right`, que pasa de 0 al ancho de
// esta franja con la misma transición. El ancho sale de `useEmpujeChat`, que
// es también el que lee el contenido para calcular su hueco: un solo número.
//
// La trampa de apilado (`project_fixed_panel_stacking_trap`) NO aplica: esa es
// para overlays montados DENTRO de una página, que quedan debajo de la sidebar
// porque el panel de contenido es `fixed`. Este componente es parte del
// cascarón y hermano de la sidebar, así que no necesita portal ni z alto para
// que se lo vea — en desktop ni siquiera se superpone con nada.
//
// EN MOBILE SÍ TAPA, y es a propósito: correr el contenido 300px en una
// pantalla de 390 no dejaría nada que mirar. Ahí el panel se muestra a pantalla
// completa por encima del cascarón (`z-[110]`, arriba del shell `z-30` y del
// header mobile de la sidebar) y el contenido no se mueve — `useEmpujeChat`
// devuelve `ancho: 0`.

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Maximize2, Minimize2, X } from 'lucide-react';
import { useChatAtlas } from '../ChatAtlasContext';
import { ConversacionChatAtlas } from './ConversacionChatAtlas';
import { MenuChat } from './MenuChat';
import { sinPantallaCompleta } from './pantallaDeRuta';
import { ESTADO_VENGO_DEL_PANEL, RUTA_CHAT_ATLAS } from './rutaChatAtlas';
import { useAccesoAlAgente } from './useAccesoAlAgente';
import { useEmpujeChat } from './useEmpujeChat';
import { useAltoDelTeclado } from './useAltoDelTeclado';

/** Lo que puede recibir foco adentro del panel, para la trampa de foco del modo
 *  mobile. Nada exótico: los controles que el chat realmente tiene. */
const SELECTOR_ENFOCABLES =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Los botones redondos del encabezado en el Asistente: el gris de "Nuevo chat" e "Historial". */
const BOTON_GRIS =
  'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-[#02062BE6] transition-colors hover:bg-neutral-200';

export const PanelChatAtlas: React.FC = () => {
  const chat = useChatAtlas();
  const navigate = useNavigate();
  const { visible, empuja, ancho } = useEmpujeChat();
  const [entrado, setEntrado] = useState(false);
  /** Agrandado en su lugar, cubriendo la pantalla (sólo con `contexto.agrandarEnElLugar`). */
  const [ampliado, setAmpliado] = useState(false);
  // El panel sobrevive al cierre lo que dura la transición. Sin esto se
  // desmontaba en el mismo frame en que `visible` pasa a false, mientras el
  // panel de contenido tarda 300ms en llevar su `right` de 420 a 0: en esa
  // franja se veía el fondo gris del contenedor raíz colapsando. Ahora el panel
  // se queda quieto desvaneciéndose y el contenido crece por debajo.
  const [montado, setMontado] = useState(visible);
  // Geometría congelada mientras sale: `useEmpujeChat` devuelve `ancho: 0` en
  // cuanto se cierra, y un panel de ancho 0 no se vería desvanecerse.
  const geometriaRef = useRef({ empuja, ancho });
  if (visible) geometriaRef.current = { empuja, ancho };
  const salida = visible ? { empuja, ancho } : geometriaRef.current;
  const trampaRef = useRef<HTMLElement | null>(null);
  // Alto que el teclado tapa desde abajo (iOS no achica el viewport): sin esto la barra de
  // entrada queda detrás del teclado.
  const tecladoAlto = useAltoDelTeclado(visible);
  const focoPrevioRef = useRef<HTMLElement | null>(null);
  // El piso de plan del Agente (ADR-2026-09-14-el-agente-es-de-pro-y-ultra). Se consulta
  // arriba de todo porque es un hook, y decide abajo junto con `montado`.
  const acceso = useAccesoAlAgente();

  useEffect(() => {
    if (!visible) {
      setEntrado(false);
      setAmpliado(false);
      return;
    }
    setMontado(true);
    const id = requestAnimationFrame(() => setEntrado(true));
    return () => cancelAnimationFrame(id);
  }, [visible]);

  // Desmontaje diferido: los 300ms son los mismos de la transición del panel de
  // contenido (`md:duration-300` en `DashboardLayout`) y los de la opacidad de
  // acá abajo. Si el chat se vuelve a abrir en el medio, el efecto de arriba
  // cancela la salida.
  useEffect(() => {
    if (visible || !montado) return;
    const id = setTimeout(() => setMontado(false), 300);
    return () => clearTimeout(id);
  }, [visible, montado]);

  // Foco: al cerrar vuelve a donde estaba el usuario (típicamente el botón
  // "Preguntále a Atlas" que lo abrió). Sin esto el foco cae a `<body>` y hay
  // que tabular la pantalla entera para volver a donde uno estaba.
  useEffect(() => {
    if (!visible) return;
    focoPrevioRef.current = (document.activeElement as HTMLElement) ?? null;
    return () => {
      focoPrevioRef.current?.focus?.();
    };
  }, [visible]);

  // Sin acceso no hay panel. Es el backstop de las puertas: ninguna de las que quedan
  // abre el chat sin plan, así que llegar acá significa que apareció un camino nuevo.
  // Sólo con el acceso CONFIRMADO: mientras carga no se dibuja (el panel aparece un
  // instante después) y denegado tampoco.
  if (!montado || acceso !== 'permitido') return null;

  const { pathname } = useLocation();
  // El panel abierto desde el Asistente de Crear (trae sus botones de encabezado): expandir lo
  // agranda en el lugar y los botones de la derecha van con el mismo fondo gris que los suyos.
  const delAsistente = !!chat.contexto?.accionesDelEncabezado;
  // Agrandar en el lugar (el Asistente de Crear): el panel se despliega hacia la izquierda hasta
  // cubrir la pantalla, con su mismo fondo, y la conversación se reacomoda al centro mientras
  // crece (ADR-2026-10-13-agrandar-el-asistente-despliega-el-panel).
  // Desde Crear el expandir NUNCA lleva al Agente: también con un contexto viejo que todavía no
  // trae la marca, alcanza con que sea el panel del Asistente (sus botones de encabezado). En el
  // teléfono el panel ya es la pantalla entera: ahí no hay botón.
  const desdeCrear = !!chat.contexto?.agrandarEnElLugar || delAsistente;
  const enElLugar = desdeCrear && salida.empuja;
  const agrandado = enElLugar && ampliado;
  const irAPantallaCompleta = () => {
    chat.cerrarChat();
    // La marca dice "vengo de expandir el panel": la pantalla dedicada
    // CONSERVA el contexto de apertura en vez de borrarlo (si no, el agente
    // deja de saber qué es "esto") y deja el panel abierto para la vuelta.
    navigate(RUTA_CHAT_ATLAS, { state: { [ESTADO_VENGO_DEL_PANEL]: true } });
  };

  // Acá no se dibuja NINGÚN gráfico: la franja mide ~420px y el HTML que
  // manda el modelo asume un lienzo cómodo. La tarjeta marca cuál abrir (el id
  // vive en el contexto, así sobrevive la navegación) y lleva a `/chat`, que
  // es la única pantalla con la columna de la derecha.
  const abrirGraficoEnPantalla = (id: string) => {
    chat.abrirGrafico(id);
    irAPantallaCompleta();
  };

  // MOBILE ES UN DIÁLOGO MODAL, Y HAY QUE DECIRLO. Con `empuja === false` el
  // panel se dibuja `fixed` de borde a borde en `z-[110]`, o sea por encima del
  // shell (z-30), de la nav de abajo (z-40) y del header de la sidebar (z-50):
  // tapa la pantalla entera. Anunciarlo como `complementary` dejaba que el
  // lector de pantalla y el tabulador recorrieran el fondo invisible. En
  // desktop, en cambio, `complementary` es lo correcto: es una columna más y no
  // tapa nada.
  const esModal = !salida.empuja;

  const alTeclado = (ev: React.KeyboardEvent<HTMLElement>) => {
    // El menú "+" frena su propio Escape con `stopPropagation`, así que con el
    // menú abierto el primer Escape lo cierra a él y el segundo al panel.
    if (ev.key === 'Escape') {
      chat.cerrarChat();
      return;
    }
    if (ev.key !== 'Tab' || !esModal) return;
    const raiz = trampaRef.current;
    if (!raiz) return;
    const focos = Array.from(
      raiz.querySelectorAll<HTMLElement>(SELECTOR_ENFOCABLES),
    ).filter((el) => !el.hasAttribute('disabled') && el.getClientRects().length > 0);
    if (focos.length === 0) return;
    const primero = focos[0];
    const ultimo = focos[focos.length - 1];
    const activo = document.activeElement as HTMLElement | null;
    if (ev.shiftKey && (activo === primero || !raiz.contains(activo))) {
      ev.preventDefault();
      ultimo.focus();
    } else if (!ev.shiftKey && activo === ultimo) {
      ev.preventDefault();
      primero.focus();
    }
  };

  return (
    <aside
      ref={trampaRef}
      role={esModal ? 'dialog' : 'complementary'}
      aria-modal={esModal || undefined}
      aria-label="Agente de Atlas"
      // Mientras sale ya no es parte de la pantalla: ni recibe clics ni se
      // anuncia. Sólo se está desvaneciendo.
      aria-hidden={!visible || undefined}
      onKeyDown={alTeclado}
      // Desktop: franja fija a la derecha, SIN sombra ni esquinas — no flota
      // por encima de nada, es una columna más del cascarón. La separa del
      // contenido una línea de 1px, como un panel dividido.
      // Mobile: pantalla completa por encima del shell (`z-[110]`).
      className={`fixed z-[110] flex flex-col bg-surface-mobile md:z-30 md:border-l md:border-[#02062B]/[0.07] md:bg-surface-content ${
        visible ? '' : 'pointer-events-none '
      }${
        salida.empuja
          ? // Empujando: el contenido ya se corrió con su propia transición de
            // 300ms; el panel sólo se desvanece, sin deslizarse. Si además
            // se trasladara, se vería un doble movimiento desacompasado.
            `${enElLugar ? 'transition-[opacity,width] duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)]' : 'transition-opacity duration-300 ease-out'} ${entrado ? 'opacity-100' : 'opacity-0'}`
          : `transition-[transform,opacity] duration-300 ease-out ${
              entrado ? 'translate-x-0 opacity-100' : 'translate-x-6 opacity-0'
            }`
      }`}
      style={{
        top: 0,
        right: 0,
        // Mobile: pantalla completa (`left: 0`, sin ancho fijo).
        // Desktop: la franja, con el MISMO ancho que el contenido se descuenta.
        left: salida.empuja ? undefined : 0,
        // Agrandado: todo el ancho, por ENCIMA del contenido (que sigue corrido lo de siempre:
        // el lienzo no se aplasta debajo).
        width: salida.empuja ? (agrandado ? '100vw' : salida.ancho) : undefined,
        bottom: tecladoAlto || 0,
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      <header className="flex h-14 shrink-0 items-center gap-1 border-b border-[#02062B]/[0.06] px-3">
        {/* Agrandado, el panel tapa el "Volver" de la pantalla: va acá, la misma pastilla
            (ADR-2026-10-13-volver-en-el-asistente-agrandado). */}
        {agrandado && chat.contexto?.alVolver && (
          <button
            type="button"
            onClick={chat.contexto.alVolver}
            aria-label="Volver"
            className="mr-1.5 inline-flex h-10 shrink-0 items-center gap-1 rounded-full bg-white pl-2 pr-3.5 shadow-[0_10px_30px_-10px_rgba(2,6,43,0.28)] ring-1 ring-black/[0.05] transition-opacity active:opacity-70 animate-fade-in"
          >
            <span aria-hidden="true" className="material-symbols-rounded text-[20px] text-black/50">
              chevron_left
            </span>
            <img src="/images/estudio-atlas-logo-solo.svg" alt="" className="h-[20px] w-auto" />
          </button>
        )}
        <p className="min-w-0 flex-1 truncate px-1 text-[14px] font-semibold text-brand-black">
          {chat.contexto?.titulo ?? 'Asistente'}
          {!chat.contexto?.titulo && chat.contexto?.pantalla && (
            <span className="ml-2 text-[12.5px] font-medium text-[#02062B]/[0.45]">
              {chat.contexto.pantalla}
            </span>
          )}
        </p>

        {/* Nuevo chat · Historial · Memoria · Consumo, en el "⋯"
            (ADR-2026-09-08-el-chat-toma-el-motor-del-asistente). */}
        {/* Los botones de la pantalla, a la vista (el Asistente de Crear: Nuevo chat e Historial). */}
        {chat.contexto?.accionesDelEncabezado}
        <MenuChat sinNuevoNiHistorial={delAsistente} estilo={delAsistente ? 'gris' : 'fantasma'} />
        {/* El Asistente de Crear agranda el panel en su lugar y lo vuelve a achicar; no lleva al
            Agente (ADR-2026-10-13-agrandar-el-asistente-despliega-el-panel). En el resto, la
            pantalla completa de siempre; en el modo Diseño, ninguno. */}
        {enElLugar ? (
          <button
            type="button"
            onClick={() => setAmpliado((a) => !a)}
            aria-label={agrandado ? 'Volver al panel con el lienzo' : 'Ver la conversación a todo el ancho'}
            title={agrandado ? 'Achicar' : 'Agrandar'}
            className={delAsistente ? BOTON_GRIS : 'inline-flex h-9 w-9 items-center justify-center rounded-full text-[#02062B]/[0.6] transition-colors hover:bg-[#02062B]/[0.05] hover:text-brand-black'}
          >
            {agrandado ? <Minimize2 className="h-[17px] w-[17px]" /> : <Maximize2 className="h-[17px] w-[17px]" />}
          </button>
        ) : (
          !desdeCrear && !sinPantallaCompleta(pathname) && (
            <button
              type="button"
              onClick={irAPantallaCompleta}
              aria-label="Abrir el chat en pantalla completa"
              title="Pantalla completa"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#02062B]/[0.6] transition-colors hover:bg-[#02062B]/[0.05] hover:text-brand-black"
            >
              <Maximize2 className="h-[17px] w-[17px]" />
            </button>
          )
        )}
        <button
          type="button"
          onClick={chat.cerrarChat}
          aria-label="Cerrar el chat"
          className={
            delAsistente
              ? BOTON_GRIS
              : 'inline-flex h-9 w-9 items-center justify-center rounded-full text-[#02062B]/[0.6] transition-colors hover:bg-[#02062B]/[0.05] hover:text-brand-black'
          }
        >
          <X className="h-5 w-5" />
        </button>
      </header>

      <ConversacionChatAtlas
        variante="panel"
        // Agrandado, la columna de siempre del medio: centrada y con un ancho de lectura.
        anchoColumna={agrandado ? 'max-w-3xl' : 'max-w-full'}
        padding="px-4"
        vistaGrafico="ir-a-pantalla"
        onAbrirGrafico={abrirGraficoEnPantalla}
      />
    </aside>
  );
};

export default PanelChatAtlas;
